import { addDays, MS_PER_DAY, startOfDay, toDateKey } from './dates.js';
import { expandRule, parseRRule } from './recurrence.js';
function occurrenceKey(event, startIso) {
    return `${event.id}@${startIso}`;
}
/**
 * Expands one event into the occurrences overlapping `window`, ascending by start.
 *
 * An occurrence "overlaps" when it starts before the window ends and ends after it
 * begins, so a multi-day event still shows on its middle days.
 */
export function expandEvent(event, window) {
    const startMs = Date.parse(event.start);
    const endMs = Date.parse(event.end);
    if (Number.isNaN(startMs) || Number.isNaN(endMs))
        return [];
    const durationMs = Math.max(0, endMs - startMs);
    const rule = parseRRule(event.recurrence);
    if (!rule) {
        if (startMs >= window.to || endMs <= window.from)
            return [];
        return [
            {
                event,
                start: event.start,
                end: event.end,
                repeat: false,
                key: occurrenceKey(event, event.start),
            },
        ];
    }
    const excluded = new Set(event.exdates.map((iso) => Date.parse(iso)));
    // Reach back by the event's duration so a long occurrence that started before the
    // window still shows inside it.
    const starts = expandRule(rule, new Date(startMs), {
        from: window.from - durationMs,
        to: window.to,
        limit: window.limitPerEvent ?? 400,
    });
    const out = [];
    for (const occurrenceStart of starts) {
        const ms = occurrenceStart.getTime();
        if (excluded.has(ms))
            continue;
        if (ms + durationMs <= window.from)
            continue;
        const startIso = occurrenceStart.toISOString();
        out.push({
            event,
            start: startIso,
            end: new Date(ms + durationMs).toISOString(),
            repeat: ms !== startMs,
            key: occurrenceKey(event, startIso),
        });
    }
    return out;
}
/** Every occurrence of every event inside `window`, sorted all-day-first then by start. */
export function expandEvents(events, window) {
    return events.flatMap((event) => expandEvent(event, window)).sort(compareOccurrences);
}
export function compareOccurrences(a, b) {
    if (a.event.allDay !== b.event.allDay)
        return a.event.allDay ? -1 : 1;
    return (Date.parse(a.start) - Date.parse(b.start) ||
        a.event.title.localeCompare(b.event.title) ||
        a.event.id.localeCompare(b.event.id));
}
/**
 * Occurrences bucketed by local `YYYY-MM-DD`, one entry per day an occurrence touches —
 * which is what the month grid and week columns index into.
 */
export function groupOccurrencesByDay(occurrences) {
    const byDay = new Map();
    for (const occurrence of occurrences) {
        const start = new Date(occurrence.start);
        const endMs = Date.parse(occurrence.end);
        // An all-day/multi-day event ending exactly at midnight belongs to the previous day.
        const lastDay = startOfDay(new Date(Math.max(endMs - 1, start.getTime())));
        for (let cursor = startOfDay(start); cursor.getTime() <= lastDay.getTime(); cursor = addDays(cursor, 1)) {
            const key = toDateKey(cursor);
            const bucket = byDay.get(key);
            if (bucket)
                bucket.push(occurrence);
            else
                byDay.set(key, [occurrence]);
            // Guard against a pathological end date producing an unbounded loop.
            if ((cursor.getTime() - start.getTime()) / MS_PER_DAY > 400)
                break;
        }
    }
    return byDay;
}
/** Members the day's occurrences involve, in first-seen order (month-cell dots). */
export function occurrenceOwnerIds(occurrences) {
    const seen = [];
    for (const occurrence of occurrences) {
        for (const ownerId of occurrence.event.ownerIds) {
            if (!seen.includes(ownerId))
                seen.push(ownerId);
        }
    }
    return seen;
}
/** `undefined`/empty filter means "Everyone". */
export function filterByMember(occurrences, memberId) {
    if (!memberId)
        return [...occurrences];
    return occurrences.filter((occurrence) => occurrence.event.ownerIds.includes(memberId));
}
//# sourceMappingURL=occurrences.js.map